import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import MyProjectPage from "./pages/MyProjectPage";
import DronesPage from "./pages/DronesPage";
import UsersPage from "./pages/UsersPage";
import LinksPage from "./pages/LinksPage";
import ChatPage from "./pages/ChatPage";
import TimePage from "./pages/TimePage";
import MapPage from "./pages/MapPage";
import LivePage from "./pages/LivePage";
import DroneControlPage from "./pages/DroneControlPage";
import StreamPage from "./pages/StreamPage";
import SettingsPage from "./pages/SettingsPage";
import PilotClockInPage from "./pages/PilotClockInPage";
import VideosPage from "./pages/VideosPage";
import SharedVideoPage from "./pages/SharedVideoPage";
import MediaPage from "./pages/MediaPage";
import SharedMediaPage from "./pages/SharedMediaPage";
import AIAssistantPage from "./pages/AIAssistantPage";
import ActivityPage from "./pages/ActivityPage";
import FlightDataPage from "./pages/FlightDataPage";
import WeeklyReportPage from "./pages/WeeklyReportPage";
import LiveTelemetryPage from "./pages/LiveTelemetryPage";
import PWAFlightPage from "./pages/PWAFlightPage";
import FlightSyncApp from "./pages/FlightSync/FlightSyncApp";
import InvoicesPage from "./pages/InvoicesPage";
import Layout from "./components/Layout";
import "./App.css";

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gold font-mono animate-pulse">CARREGANDO...</div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
};

const AppRoutes = () => {
  const { user } = useAuth();
  
  const getInitialRoute = () => {
    if (!user) return '/dashboard';
    if (user.role === 'pilot') return '/clock-in';
    if (['supervisor', 'leader', 'vigilant'].includes(user.role)) return '/my-project';
    return '/dashboard';
  };
  
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={getInitialRoute()} replace /> : <LoginPage />} />
      <Route path="/stream/:token" element={<StreamPage />} />
      <Route path="/video/:shareToken" element={<SharedVideoPage />} />
      <Route path="/media/:shareToken" element={<SharedMediaPage />} />
      <Route path="/pwa-flight" element={<PWAFlightPage />} />
      <Route path="/flightsync/*" element={<FlightSyncApp />} />
      
      {/* Pilot Clock-In Gate (LGPD) */}
      <Route path="/clock-in" element={
        <ProtectedRoute allowedRoles={["pilot"]}>
          <PilotClockInPage />
        </ProtectedRoute>
      } />
      
      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to={user?.role === 'pilot' ? '/clock-in' : ['supervisor', 'leader', 'vigilant'].includes(user?.role) ? '/my-project' : '/dashboard'} replace />} />
        <Route path="dashboard" element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "supervisor", "leader", "vigilant"]}>
            <DashboardPage />
          </ProtectedRoute>
        } />
        <Route path="projects" element={
          <ProtectedRoute allowedRoles={["super_admin"]}>
            <ProjectsPage />
          </ProtectedRoute>
        } />
        <Route path="projects/:projectId" element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "supervisor"]}>
            <ProjectDetailPage />
          </ProtectedRoute>
        } />
        <Route path="my-project" element={
          <ProtectedRoute allowedRoles={["admin", "supervisor", "pilot", "leader", "vigilant"]}>
            <MyProjectPage />
          </ProtectedRoute>
        } />
        <Route path="drones" element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "supervisor"]}>
            <DronesPage />
          </ProtectedRoute>
        } />
        <Route path="users" element={
          <ProtectedRoute allowedRoles={["super_admin"]}>
            <UsersPage />
          </ProtectedRoute>
        } />
        <Route path="links" element={
          <ProtectedRoute allowedRoles={["super_admin"]}>
            <LinksPage />
          </ProtectedRoute>
        } />
        <Route path="chat" element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "supervisor", "pilot", "leader", "vigilant"]}>
            <ChatPage />
          </ProtectedRoute>
        } />
        <Route path="time" element={<TimePage />} />
        <Route path="videos" element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "supervisor", "pilot"]}>
            <MediaPage />
          </ProtectedRoute>
        } />
        <Route path="live" element={
          <ProtectedRoute allowedRoles={["super_admin"]}>
            <LivePage />
          </ProtectedRoute>
        } />
        <Route path="control" element={
          <ProtectedRoute allowedRoles={["super_admin"]}>
            <DroneControlPage />
          </ProtectedRoute>
        } />
        <Route path="map" element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "supervisor", "leader", "vigilant"]}>
            <MapPage />
          </ProtectedRoute>
        } />
        <Route path="ai" element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "supervisor", "pilot", "leader", "vigilant"]}>
            <AIAssistantPage />
          </ProtectedRoute>
        } />
        <Route path="activity" element={
          <ProtectedRoute allowedRoles={["super_admin"]}>
            <ActivityPage />
          </ProtectedRoute>
        } />
        <Route path="flight-data" element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "supervisor"]}>
            <FlightDataPage />
          </ProtectedRoute>
        } />
        <Route path="reports/weekly" element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "supervisor"]}>
            <WeeklyReportPage />
          </ProtectedRoute>
        } />
        <Route path="live-telemetry" element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "supervisor"]}>
            <LiveTelemetryPage />
          </ProtectedRoute>
        } />
        <Route path="prestacao-contas" element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "supervisor", "pilot", "leader", "vigilant"]}>
            <InvoicesPage />
          </ProtectedRoute>
        } />
      </Route>
      
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster 
          position="top-right"
          toastOptions={{
            style: {
              background: '#fff',
              border: '1px solid #e5e7eb',
              color: '#111',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
